import { CommonEvents, Preferences } from '@3ds/common-preferences'
import { nextTick } from 'vue'

import { useCommunitiesStore } from '~/entities/container/community/stores/communities'

import type { IChildWindow, IMultiWindowAppNativeEvent } from '~/features-swym/multi-window/interfaces/multi-window'
import { useMultiWindowStore } from '~/features-swym/multi-window/store/multi-window'
import { getMultiWindowId, isMultiWindowActive, openNewWindowWithUrl, refineMultiWindowPath } from '~/features-swym/multi-window/utils/multi-window'

import { useContentContextManager, usePageContext } from '~/features-shared/config-manager/composables/context-manager'
import { EContentContext, IPageContext } from '~/features-shared/config-manager/interfaces/contexts'
import { logger } from '~/features-shared/libs/utils/logger'

import { toggleFeature, useFeatureManager } from '~/features-generic/config-manager/composables/feature-manager'
import { useTranslations } from '~/features-generic/translations/composables/translations'

import type { RoutesName } from '~/typings/vue-router'
/**
 * Id of the window
 * - if the window is not a child window it would be equal to null
 */
export const multiWindowId = ref<string | null>(null)
/**
 * Base url of the window
 * - if the window is not a child window it would be equal to null
 */
export const multiWindowBaseUrl = ref<string | undefined>()

/**
 * Context on witch the a child window was open
 */
export const multiWindowContext = ref<RoutesName | typeof IPageContext | undefined>()

/**
 * Check if the multiWindow is a facet one
 */
export const isFacetMultiWindow = ref<boolean>(false)

/**
 * Broadcast chanel that can communicate with all the window for multiWindow purpose
 * - Channel : swym-multiWindow
 */
export const multiWindowBroadcast = new BroadcastChannel('swym-multiWindow')

const storeMultiWindow = useMultiWindowStore()

/**
 * Init multi window
 * - init swym-multiWindow BroadcastChannel
 * - check if current window is a child or not
 * - prepare event emit on window unload
 * - toggle off the feature manager for topbar, navbar & left column if we are in a child window
 */
export function initMultiWindow() {
  logger.info('multi-window:init')
  const route = useRoute()
  const { currentPageMeta } = usePageContext()
  const { context } = useContentContextManager()
  /**
   * Why use sessionStorage & window.isChild ?
   * - window.isChild will be used for first time the child window is open, in case of refresh of this child window (browser context) we will loose the isChild property but not the sessionStorage
   * - sessionStorage is latter set to isChild true and will prevent to loose the fact that the window is a child one
   */

  whenever(isMultiWindowActive, () => {
    multiWindowBaseUrl.value = refineMultiWindowPath(route.fullPath)

    multiWindowId.value = getMultiWindowId(
route.path as string, route.query?.content as string || route.query?.media as string) || null
    multiWindowBroadcast.postMessage({
      event: 'multi-window:add',
      payload: { pathContext: route.path, winId: multiWindowId.value },
    })
    document.body.classList.add('multi-window-context')
    sessionStorage.setItem('isChild', 'true')
  }, { immediate: true })

  window.addEventListener('beforeunload', () => {
    multiWindowBroadcast.postMessage({
      event: 'multi-window:delete',
      payload: { winId: multiWindowId.value },
    })
  })

  multiWindowBroadcast.addEventListener('message', (e) => {
    const payload = e.data.payload
    const event = e.data.event
    if (event === 'multi-window:add'
      && storeMultiWindow.getChildWindow(payload.winId) !== multiWindowId.value
      && !storeMultiWindow.childWindows.has(payload.winId)
    ) {
      logger.info('multi-window:add', payload.winId)
      storeMultiWindow.setChildWindow(payload.winId, null)
    }

    if (event === 'multi-window:delete') {
      logger.info('multi-window:delete', payload.winId)
      storeMultiWindow.removeChildWindow(payload.winId)
    }

    if (event === 'multi-window:focus' && payload.winId === multiWindowId.value) {
      logger.info('multi-window:focus', payload.winId)
      window.focus()
    }

    if (event === 'multi-window:debug') {
      logger.debug('multi-window:debug store =>', storeMultiWindow.childWindows)
      logger.debug('multi-window:debug windowID =>', multiWindowId.value)
      logger.debug('multi-window:debug windowContext =>', multiWindowContext.value)
    }
  })

  whenever(isMultiWindowActive, () => {
    // Important : as we don't have the left panel open we don't have all the information from the other communities (this information could be used by the profile lightbox or link preview)
    const storeCommunity = useCommunitiesStore()
    storeCommunity.fetchList()

    if (currentPageMeta.value.filteredBy.value && currentPageMeta.value.name) {
      isFacetMultiWindow.value = true
      multiWindowContext.value = `${currentPageMeta.value.name}-${currentPageMeta.value.filteredBy.value}` as unknown as typeof IPageContext
    }
    else if (context.value === EContentContext.Question) {
      isFacetMultiWindow.value = true
      multiWindowContext.value = `${currentPageMeta.value.name}-${context.value}`
    }
    else {
      multiWindowContext.value = currentPageMeta.value.name as typeof IPageContext
    }

    toggleFeature('topbar.enabled.desktop', false)
    toggleFeature('layout.nav_bar', false)
    toggleFeature('layout.left_column', false)

    // intercept navigation in a multi-window context
    const router = useRouter()

    router.beforeEach((to, from, next) => {
      /*
      *  How does this work if we are in the context of a new window, we intercept the navigation, if the navigation is in the same context/path we keep the user in the same window if we are not in the same context/path we open a new window
      */
      const refinedToFullPathUrl = refineMultiWindowPath(to.fullPath)
      // Keep user in the current multi-window
      if (multiWindowBaseUrl.value && refinedToFullPathUrl.includes(multiWindowBaseUrl.value)) {
        next()
        return
      }

      // We for now prevent at all cost a redirection to /dm , sometimes redirection happen when a content is not loaded and the message "This content does not exist" appear to the user
      if (refinedToFullPathUrl === '/dm')
        return

      /*
       * When a search content is open in a new window we do a redirection to the actual searched content. This should not trigger an open in a new window as the current child window was open by the desktopApp, so we need to make this redirection the new context of the opened window !
       */
      if (from.fullPath.includes('#content:X3DContentId')) {
        multiWindowBaseUrl.value = refineMultiWindowPath(to.fullPath)
        multiWindowId.value = getMultiWindowId(to.path, to.query?.content as string || to.query?.media as string)
        next()
        return
      }
      openNewWindowWithUrl({
        winId: btoa(to.fullPath),
        winUrl: to.fullPath,
        winOpts: 'width=1000,height=500,left=290,top=460',
      })
    })
  }, { immediate: true })

  // Listen to native event when on native apps
  CommonEvents.listenEventFromNativeApp(CommonEvents.EVENTS.ON_APP_MULTIWINDOWS, (e: IMultiWindowAppNativeEvent) => {
    if (e.event === CommonEvents.EVENTS.NOTIFY_MULTIWINDOW_ADD) {
      logger.info('multi-window:add', e.winId)
      storeMultiWindow.setChildWindow(e.winId, null)
    }
    if (e.event === CommonEvents.EVENTS.NOTIFY_MULTIWINDOW_DELETE) {
      logger.info('Native multi-window:delete', e.winId)
      storeMultiWindow.removeChildWindow(e.winId)
    }
  })
}

/**
 * Composable to handle multiWindow
 *
 */
export function useMultiWindow() {
  const { $i18n } = useTranslations('multi-window')
  const isMobileDevice = useFeatureManager('app.config.mobile_device')
  const hasSupportForMultiWindow = useFeatureManager('app.config.multi_window.has_support')
  const isMultiWindowActivatedForBrowser = useFeatureManager('app.config.multi_window.browser')
  const isMultiWindowActivatedForDesktopApp = useFeatureManager('app.config.multi_window.desktop')
  const isMultiWindowActivatedForAppleVision = useFeatureManager('app.config.multi_window.apple_vision')
  const route = useRoute()

  /**
   *  Check if the MultiWindow button is available or not
   * @param id the id of the current window (can be the subject_uri or the subject_uri/filter )
   * @returns A boolean of where or not we multiWindow is available
   */
  function isMultiWindowAvailable(id: string): boolean {
    /*
     * Feature manager check
     * - check featureManager for Desktop App
     * - then check featureManager if it's native App to match Apple vision
     * - then check for mobile App (multi_window will always be disable for mobile app)
     * - finally check featureManager for browser
     */
    if (Preferences.isDesktopApp()) {
      if (!hasSupportForMultiWindow.value)
        return false
      if (!isMultiWindowActivatedForDesktopApp.value)
        return false
    }
    else if (Preferences.isNativeApp()) {
      if (!hasSupportForMultiWindow.value)
        return false
      if (!isMultiWindowActivatedForAppleVision.value)
        return false
    }
    else if (isMobileDevice.value) { return false }
    else if (!isMultiWindowActivatedForBrowser.value) { return false }

    // check current window id/path
    if (multiWindowContext.value === IPageContext.CommunityFiltered)
      return false

    return multiWindowId.value !== id
  }

  /**
   * Open a new child window
   * @param winUrl Url of the new window
   * @param winRefId Not the id can be can be the subject_uri or the subject_uri/filter, and it is preferable to add one and not set it to undefined
   */
  async function openChildWindow(winUrl: string, winRefId?: string) {
    try {
      await nextTick()
      if (winUrl) {
        const winId = winRefId || btoa(winUrl)

        // Note even if it's duplicate native app will check natively if it exist or not, and will decide if the already existing window will be focus or not
        if (storeMultiWindow.childWindows.has(winId) && !Preferences.isNativeApp()) {
          multiWindowBroadcast.postMessage({
            event: 'multi-window:focus',
            payload: { winId },
          })
        }
        else {
          const windowRef = openNewWindowWithUrl({
            winId,
            winUrl,
            winOpts: 'width=1000,height=500,left=290,top=460',
          })

          // eslint-disable-next-line max-depth
          if (windowRef) {
            (windowRef.window as IChildWindow).isChild = true
            storeMultiWindow.setChildWindow(winId, windowRef)
          }
        }
      }
    }
    catch (error) {
      logger.error($i18n('CannotInstantiateChildWindow'), error)
    }
  }

  /**
   *  Check if we show the back arrow icon-button in content lightbox
   */
  const isBackNavigationDisable = computed(() => {
    return multiWindowContext.value !== undefined && route.query.content === multiWindowId.value
  })

  // TODO : pds9 wait for new version of vuekit to fix hideCloseIcon prop not working on vu-lightbox, opened IR: IR-1323188
  /**
   * Check we need to show the media lightbox close icon-button
   */
  const isCloseNavigationDisable = computed(() => {
    return multiWindowContext.value !== undefined && multiWindowId.value === route.query.media
  })

  return {
    isMultiWindowAvailable,
    isBackNavigationDisable,
    isCloseNavigationDisable,
    openChildWindow,
  }
}
